import { ICommon, TimeUnit, TokenResponseMethod } from 'types/common';
import { AuthMethodRoute } from 'constants/routes';

export interface IProjectSettings extends ICommon {
	projectId: string;
	projectName: string;
	domain?: string;
	refreshTokenExpiration: number;
	refreshTokenExpirationUnit: TimeUnit;
	stepupTokenExpiration: number;
	stepupTokenExpirationUnit: TimeUnit;
	tokenResponseMethod: TokenResponseMethod;
	projectSelfProvisioning?: boolean;
	inviteURL?: string;
	trustedDomains?: string;
	hasNoActivity?: boolean;
}

export interface ICompany extends ICommon {
	id: string;
	name: string;
	maxAllowedProjects: number;
	supportConcent?: boolean;
}

export interface IProjectCreate {
	name: string;
}

export enum SignUpPermissions {
	All = 'all',
	InvitedUsersAndSSOAllowed = 'invitedUsersAndSSOAllowed'
}

export interface ITenantCreationFields {
	name: string;
	id?: string;
	logo?: string;
	selfProvisioningDomains: string[];
}

export enum DescopeSSOMappingsAttributes {
	Email = 'email',
	Name = 'name',
	PhoneNumber = 'phoneNumber',
	group = 'group'
}

export interface ISwitchableSettings {
	enabled: boolean;
}

export interface IAuthMethodsSettings {
	magiclink: IMagicLinkSettings;
	enchantedlink: IMagicLinkSettings;
	otp: IOTPSettings;
	oauth: ISocialSettings;
	totp: ITOTPSettings;
	saml: ISSOSettings;
	oidc: IOIDCSettings;
	webauthn: IWebAuthnSettings;
	password: IPasswordSettings;
}

export interface ICommonSwitchableSettings
	extends ISwitchableSettings,
		ICommon {}

export interface IOneTimeProviderSettings extends ICommonSwitchableSettings {
	textServiceProvider: string;
	textMessageBody: string;
	emailServiceProvider: string;
	emailSubject: string;
	emailBody: string;
}

export interface IOneTimeSettings
	extends ICommonSwitchableSettings,
		IOneTimeProviderSettings {
	expirationTime: number;
	expirationTimeUnit: TimeUnit;
}

export interface IMagicLinkSettings extends IOneTimeSettings {
	redirectURL: string;
}

export interface IOTPSettings extends IOneTimeSettings {
	domain: string;
}

export interface ITOTPSettings extends ICommonSwitchableSettings {}

export interface IWebAuthnSettings extends ICommonSwitchableSettings {
	relyingPartyId?: string;
}

export interface ISSOMapping {
	name?: string;
	email?: string;
	phoneNumber?: string;
	group?: string;
}

export enum SSOTypes {
	sp = 'sp',
	oidc = 'oidc'
}

export interface IOIDCSettings extends ICommonSwitchableSettings {
	redirectURL?: string;
}

export interface ISSOSettings extends ICommonSwitchableSettings {
	redirectURL?: string;
	idpURL?: string;
	idpMetadataURL?: string;
	idpCert?: string;
	entityID?: string;
	descopeEntityID?: string;
	signRequest?: boolean;
	useMetaInfoToggle?: boolean;
	allowSamlAuthOnly?: boolean;
	acsUrl?: string;
	domain?: string;
}

export interface IPasswordSettings extends ICommonSwitchableSettings {
	enabled: boolean;
	minLength: number;
	lowercase: boolean;
	uppercase: boolean;
	number: boolean;
	nonAlphanumeric: boolean;
	expirationWeeks: number;
	expiration: boolean;
	reuse: boolean;
	reuseAmount: number;
	lock: boolean;
	lockAttempts: number;
	emailServiceProvider: string;
	emailBody: string;
	emailSubject: string;
	resetAuthMethod: AuthMethodRoute;
	textServiceProvider: string;
	textMessageBody: string;
}

export interface ISocialProviderSettings extends ISwitchableSettings {
	useSelfAccount: boolean;
	clientID?: string;
	clientSecret?: string;
	redirectURL?: string;
	scope?: string[];
	trustProvidedEmails?: boolean;
	callbackUrl?: string;
}

export interface ISocialProviderSettingsWrapper
	extends ISocialProviderSettings {
	callbackDomain?: string;
}

export interface ISocialSettings extends ICommonSwitchableSettings {
	redirectUrl?: string;
	callbackDomain?: string;
	callbackUrl?: string;
	providerSettings?: {
		[key: string]: ISocialProviderSettings;
	};
}

export enum CompanyPageTabs {
	settings = 'settings',
	admins = 'admins',
	managementKeys = 'managementKeys'
}

export interface ISSOGroupMappingsItem {
	role: { id: string; name: string };
	groups: string[];
	id: string;
}

export interface ISSOGroupMappings {
	mappings: ISSOGroupMappingsItem[] | undefined;
}

export interface ISSOMappings extends ISSOGroupMappings {
	samlMappings?: ISSOMapping;
}
